import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, ShoppingCart, Check, Upload, ArrowLeft, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedPaperType, setSelectedPaperType] = useState('');
  const [selectedQuantityTier, setSelectedQuantityTier] = useState(null);
  const [designFile, setDesignFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/api/products/${id}`);
        setProduct(res.data);
        if (res.data.sizes?.length > 0) setSelectedSize(res.data.sizes[0].name);
        if (res.data.paper_types?.length > 0) setSelectedPaperType(res.data.paper_types[0].name);
        if (res.data.quantities?.length > 0) setSelectedQuantityTier(res.data.quantities[0]);
      } catch {
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (!user || !id) return;
    api.get('/api/wishlist').then(res => {
      setWishlisted(res.data.some(item => item.product_id === id));
    }).catch(() => {});
  }, [user, id, api]);

  const toggleWishlist = async () => {
    if (!user) { toast.error('Please login first'); navigate('/login'); return; }
    try {
      if (wishlisted) {
        await api.delete(`/api/wishlist/${id}`);
        setWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/api/wishlist/${id}`);
        setWishlisted(true);
        toast.success('Added to wishlist!');
      }
    } catch {
      toast.error('Failed to update wishlist');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!user) {
      toast.error('Please login to upload designs');
      navigate('/login');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/upload', formData);
      setUploadedFileId(data.id);
      setDesignFile(file);
      toast.success('Design uploaded successfully');
    } catch {
      // Upload failed
      toast.error('Failed to upload design');
    } finally {
      setUploading(false);
    }
  };

  const calculatePrice = () => {
    if (!product) return 0;
    let price = product.base_price;

    if (selectedSize) {
      const sizeOpt = product.sizes?.find((s) => s.name === selectedSize);
      if (sizeOpt) price = sizeOpt.price;
    }

    if (selectedPaperType) {
      const paperOpt = product.paper_types?.find((p) => p.name === selectedPaperType);
      if (paperOpt) price += paperOpt.price_modifier || 0;
    }

    if (selectedQuantityTier) {
      price = selectedQuantityTier.price_per_unit;
    }

    return price;
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    setAdding(true);
    const options = {
      size: selectedSize,
      paper_type: selectedPaperType,
      quantity_tier: selectedQuantityTier?.amount,
    };

    const success = await addToCart(product.id, quantity, options, uploadedFileId);
    setAdding(false);

    if (success) {
      toast.success('Added to cart!');
    } else {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const unitPrice = calculatePrice();
  const totalPrice = unitPrice * quantity * (selectedQuantityTier?.amount || 1);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="bg-white rounded-3xl overflow-hidden border border-gray-200 sticky top-24">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full aspect-square object-cover"
              />
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <span className="text-sm text-coral uppercase tracking-[0.2em] font-semibold">
                {product.category}
              </span>
              <div className="flex items-start justify-between gap-3 mt-2">
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">
                  {product.name}
                </h1>
                <button
                  onClick={toggleWishlist}
                  className="mt-1 p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                  data-testid="wishlist-toggle-btn"
                >
                  <Heart className={`h-6 w-6 transition-colors ${wishlisted ? 'text-coral fill-coral' : 'text-gray-300'}`} />
                </button>
              </div>
              <p className="mt-4 text-gray-600 leading-relaxed">{product.description}</p>
            </div>

            {/* Size Selection */}
            {product.sizes?.length > 0 && (
              <div>
                <Label className="text-navy font-medium mb-3 block">Size</Label>
                <RadioGroup
                  value={selectedSize}
                  onValueChange={setSelectedSize}
                  className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                >
                  {product.sizes.map((size) => (
                    <Label
                      key={size.name}
                      className={`flex items-center justify-center px-4 py-3 border rounded-xl cursor-pointer transition-all ${
                        selectedSize === size.name
                          ? 'border-mint bg-mint/10 text-navy'
                          : 'border-gray-200 hover:border-mint/50'
                      }`}
                    >
                      <RadioGroupItem value={size.name} className="sr-only" />
                      <span className="text-sm font-medium">{size.name}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Paper Type Selection */}
            {product.paper_types?.length > 0 && (
              <div>
                <Label className="text-navy font-medium mb-3 block">Paper Type</Label>
                <RadioGroup
                  value={selectedPaperType}
                  onValueChange={setSelectedPaperType}
                  className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                >
                  {product.paper_types.map((paper) => (
                    <Label
                      key={paper.name}
                      className={`flex items-center justify-between px-4 py-3 border rounded-xl cursor-pointer transition-all ${
                        selectedPaperType === paper.name
                          ? 'border-mint bg-mint/10 text-navy'
                          : 'border-gray-200 hover:border-mint/50'
                      }`}
                    >
                      <RadioGroupItem value={paper.name} className="sr-only" />
                      <span className="text-sm font-medium">{paper.name}</span>
                      {paper.price_modifier > 0 && (
                        <span className="text-xs text-gray-500">+${paper.price_modifier}</span>
                      )}
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Quantity Tiers */}
            {product.quantities?.length > 0 && (
              <div>
                <Label className="text-navy font-medium mb-3 block">Quantity</Label>
                <RadioGroup
                  value={selectedQuantityTier?.amount?.toString()}
                  onValueChange={(val) => {
                    const tier = product.quantities.find((q) => q.amount.toString() === val);
                    setSelectedQuantityTier(tier);
                  }}
                  className="grid grid-cols-2 sm:grid-cols-4 gap-3"
                >
                  {product.quantities.map((qty) => (
                    <Label
                      key={qty.amount}
                      className={`flex flex-col items-center px-4 py-3 border rounded-xl cursor-pointer transition-all ${
                        selectedQuantityTier?.amount === qty.amount
                          ? 'border-mint bg-mint/10 text-navy'
                          : 'border-gray-200 hover:border-mint/50'
                      }`}
                    >
                      <RadioGroupItem value={qty.amount.toString()} className="sr-only" />
                      <span className="font-semibold">{qty.amount}</span>
                      <span className="text-xs text-gray-500">${qty.price_per_unit}/ea</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Design Upload */}
            <div>
              <Label className="text-navy font-medium mb-3 block">Upload Your Design (Optional)</Label>
              <label className="relative block border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-mint/50 transition-colors cursor-pointer">
                {designFile ? (
                  <div className="flex items-center justify-center gap-2 text-mint">
                    <Check className="h-5 w-5" />
                    <span className="font-medium">{designFile.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Click to upload your design
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      PDF, PNG, JPG up to 10MB
                    </p>
                  </>
                )}
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="sr-only"
                  disabled={uploading}
                  data-testid="design-upload-input"
                />
              </label>
            </div>

            {/* Quantity Selector & Add to Cart */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Unit Price:</span>
                <span className="font-semibold text-navy">${unitPrice.toFixed(2)}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Copies:</span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="h-8 w-8"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center font-semibold">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                    className="h-8 w-8"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
                <span className="text-lg font-medium text-navy">Total:</span>
                <span className="text-2xl font-bold text-navy">${totalPrice.toFixed(2)}</span>
              </div>

              <Button
                onClick={handleAddToCart}
                disabled={adding}
                className="w-full bg-mint hover:bg-mint-dark text-black py-6 text-base rounded-full btn-hover"
                data-testid="add-to-cart-btn"
              >
                {adding ? (
                  'Adding...'
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    Add to Cart
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
